import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from "@angular/core";

import {CheckboxListItem} from "../../../components/checkbox-list/checkbox-list.component";
import {DetailViewMode} from "../plan-detail-v5/plan-detail-v5.component";
import {ParamsConfig} from "../common/validator-factory";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {TranslateService} from "@ngx-translate/core";
import {ResizeAwareService} from '../../../services/resizeaware.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import { getLocaleNumberSymbol, NumberSymbol } from "@angular/common";
import { TenantService } from "../../../tenant.service";
import { AuthenticationService } from "../../../authentication.service";
import { Features } from "../../../models/plan";
import {EChartsConfigService} from '../../../services/echarts-config.service';
import {ChartExportService} from '../../../services/chart-export.service';
import {EChartsDragHandler} from '../common/echarts-drag-handler';
import type { ECharts, EChartsOption } from 'echarts';

export enum SidebarVisibility{
  Hidden="Hidden",
  Visible="Visible",
  Float="Float"
}

//ignore
@Component({
  standalone: false,
  selector: "plan-detail-chart-v2",
  templateUrl: "./plan-detail-chart-v2.component.html",
  styleUrls: ["./plan-detail-chart-v2.component.scss"],
  //animations
})
export class PlanDetailChartV2Component implements OnInit, OnDestroy {
  @ViewChild("chartEl") chartEl: ElementRef;
  @Input() chartId = null;
  @Input() displayFooter = true;
  @Input() displayHeader = true;
  @Input() isMultiChart = false;
  @Input() locale = "en-US";

  @Output() pointHovered: EventEmitter<any> = new EventEmitter<any>();
  @Output() pointOut: EventEmitter<any> = new EventEmitter<any>();
  @Output() setExtremes: EventEmitter<any> = new EventEmitter<any>();
  @Output() paramsListChanged: EventEmitter<any> = new EventEmitter<any>();
  private ignoreHighlight=false;
  private ignoreExtremes=false;
  private ignoreRemoveHighlight: boolean;
  private refreshTimer = null;
  private _isUpdatingChart = false;
  private unsubscribe$: Subject<void> = new Subject();
  private dragHandler: EChartsDragHandler;
  private decimalPoint = '.';
  private thousandsSep = ',';

  @Input() set rowData(rows) {
    this._rowData = rows;

    this.updateParamsList();
    this.paramsListChanged.emit(this.paramsList);

    this.updateChart();
  }

  @Input() set isLoading(value){
    this._isLoading = value;
    if(!this.chart) return;

    if(value){
      this.chart.showLoading('default', { text: this.loadingText, maskColor: 'rgba(255, 255, 255, 0.8)' });
    }else{
      this.chart.hideLoading();
    }
  }
  _isLoading = false;

  @Input() set columnDefs(columns) {
    this._columnDefs = columns;
    this.updateChart();
  }
  @Input() set isVisible(value: boolean) {
    const wasHidden = !this._isVisible;
    this._isVisible = value;
    if (wasHidden && value && this.chart) {
      setTimeout(() => {
        this.chart.resize();
        this.updateChart();
      });
    }
  }
  get isVisible() { return this._isVisible; }
  _isVisible = false;

  @Input() set viewMode(viewMode: DetailViewMode) {
    this._viewMode = viewMode;
    this._shouldResetZoom=true;
    this.updateChart();
  }

  @Input() set contactType(ctMU){
    if(ctMU==="MU" && this._contactType!="MU"){
      this._contactType="MU";
      this.updateChart();
    }else if(ctMU=="CT" && this._contactType!="CT"){
      this._contactType="CT";
      this.updateChart();
    }
  }

  get contactType(){
    return this._contactType;
  }

  get viewMode(){
    return this._viewMode;
  }

  @Input() gridContext;
  @Input() sidebar:SidebarVisibility=SidebarVisibility.Visible;

  @Output() drillDown: EventEmitter<any> = new EventEmitter<any>();
  @Output() rollUp: EventEmitter<any> = new EventEmitter<any>();
  @Output() displayParams: EventEmitter<any> = new EventEmitter<any>();

  startMax = 0;
  _rowData: any = null;
  _columnDefs: any = null;
  _colDefsFlat:any = null;
  _viewMode: DetailViewMode;
  _shouldResetZoom = false;
  _rowsParamMap = {};
  _skipAnimation=false;
  _hoveredPoint = null;
  _yaxis=0;
  _isTranslationReady=false;
  chart: ECharts = null;
  chartMergeOptions: any = {};
  paramsList: CheckboxListItem[] = [];
  _contactType="CT";
  lastExtremesSet=0;
  isDragging=false;
  isZoomed = false;
  showExportMenu = false;
  resetZoomText = 'Reset zoom';
  loadingText = 'Loading...';
  exportLabels: any = {};
  public _newHireSettingsFeature: boolean = false;
  selectedParams={
    "CT":{
      "CONTACTS":true
    },
    "MU":{
      // Will be set dynamically based on _newHireSettingsFeature
    }
  };
  primaryAxisParams={
    "CT":{

    },
    "MU":{

    }
  }

  chartOptions: EChartsOption;

  constructor(
    private modalService: NgbModal,
    private translateSrv: TranslateService,
    private resizeAwareService: ResizeAwareService,
    public tenantService: TenantService,
    private authService: AuthenticationService,
    private echartsConfig: EChartsConfigService,
    private chartExportService: ChartExportService
  ) {
    // Initialize new hire feature flag
    this._newHireSettingsFeature = authService.hasFeature(Features.NEW_HIRE_SETTINGS);

    // Set default MU parameter based on new hire feature flag
    this.initializeMUSelectedParams();

    translateSrv.get([
      "chart.btn.chart.menu",
      "plan.chart.resetZoom",
      "plan.chart.resetZoomTitle",
      "plan.chart.exitFullscreen",
      "plan.chart.viewFullscreen",
      "plan.chart.printChart",
      "plan.chart.downloadJPEG",
      "plan.chart.downloadPDF",
      "plan.chart.downloadPNG",
      "plan.chart.downloadSVG",
      "plan.chart.loading",
    ])
      .subscribe((keys=>{
        this.resetZoomText = keys["plan.chart.resetZoom"] || 'Reset zoom';
        this.loadingText = keys["plan.chart.loading"] || 'Loading...';
        this.exportLabels = {
          contextButtonTitle: keys["chart.btn.chart.menu"],
          exitFullscreen: keys["plan.chart.exitFullscreen"],
          viewFullscreen: keys["plan.chart.viewFullscreen"],
          printChart: keys["plan.chart.printChart"],
          downloadJPEG: keys["plan.chart.downloadJPEG"],
          downloadPDF: keys["plan.chart.downloadPDF"],
          downloadPNG: keys["plan.chart.downloadPNG"],
          downloadSVG: keys["plan.chart.downloadSVG"],
        };
        this._isTranslationReady=true;
    }));
  }

  /**
   * Initializes the default selected parameter for MU view based on new hire feature flag
   */
  private initializeMUSelectedParams(): void {
    if (this._newHireSettingsFeature === false) {
      // When new hire feature is OFF, default to ACTUAL_FTE_rollup
      this.selectedParams["MU"]["ACTUAL_FTE_rollup"] = true;
    } else {
      // When new hire feature is ON, default to ACTUAL_FTE_TOTAL_rollup
      this.selectedParams["MU"]["ACTUAL_FTE_TOTAL_rollup"] = true;
    }
  }

  ngOnInit() {
    this.decimalPoint = getLocaleNumberSymbol(this.locale, NumberSymbol.Decimal);
    this.thousandsSep = getLocaleNumberSymbol(this.locale, NumberSymbol.Group);

    this.loadParamSelection();
    this.migrateMuParameterSelection();
    this.initChartOptions();
    this.resizeAwareService.Resized.pipe(takeUntil(this.unsubscribe$)).subscribe(()=>{
      if(this.chart){
        this.chart.resize();
        const series = (this.chart.getOption().series as any[]) || [];
        this.chart.setOption({ grid: { bottom: this.getLegendGridBottom(series, this.chart.getWidth()) } } as any);
      }
    });
  }

  ngOnDestroy(){
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.dragHandler) {
      this.dragHandler.destroy();
    }
  }

  private initChartOptions() {
    this.chartOptions = this.echartsConfig.getDefaultChartOptions(this.locale);
  }

  onChartInit(chart: ECharts) {
    if (this.chart) return;
    this.chart = chart;

    // Apply loading state if it was set before chart init
    if (this._isLoading) {
      this.chart.showLoading('default', { text: this.loadingText, maskColor: 'rgba(255, 255, 255, 0.8)' });
    }

    // Setup drag handler
    this.dragHandler = new EChartsDragHandler(chart, {
      onDragStart: (seriesIndex, dataIndex) => {
        this.isDragging = true;
      },
      onDrag: (seriesIndex, dataIndex, newValue) => {
        if (!this.isMultiChart) {
          this.chart.dispatchAction({ type: 'showTip', seriesIndex, dataIndex });
        }
      },
      onDrop: (seriesIndex, dataIndex, newValue) => {
        this.isDragging = false;
        this.onValueSet(seriesIndex, dataIndex, newValue);
      }
    });

    // Listen for x-axis label clicks for drilldown
    chart.on('click', 'xAxis', (params: any) => {
      this.onDrillDown();
    });

    // Listen for datazoom events to track zoom state and sync
    chart.on('datazoom', (params: any) => {
      const option = chart.getOption() as any;
      const dataZoom = option.dataZoom;
      if (dataZoom && dataZoom.length > 0) {
        this.isZoomed = dataZoom[0].start > 0 || dataZoom[0].end < 100;
      }
      if (!this.ignoreExtremes) {
        this.setExtremes.emit({
          start: dataZoom?.[0]?.start,
          end: dataZoom?.[0]?.end
        });
      }
      this.ignoreExtremes = false;
    });

    // Listen for mouseover events for cross-chart sync and crosshair line
    chart.on('mouseover', 'series', (params: any) => {
      if (this.isDragging) return;
      this._hoveredPoint = params;

      // Draw vertical crosshair line at hovered point
      this.showCrosshair(params);

      if (this.ignoreHighlight) {
        this.ignoreHighlight = false;
        return;
      }
      this.pointHovered.emit(params);
    });

    chart.on('mouseout', 'series', (params: any) => {
      // Remove crosshair line
      this.hideCrosshair();

      if (this.ignoreHighlight) {
        this.ignoreHighlight = false;
        return;
      }
      this.pointOut.emit(params);
    });

    // Defer initial update to ensure all Angular inputs are processed
    setTimeout(() => {
      this.chart.resize();
      this.updateChart();
    });
  }

  private updateParamsList() {
    let rows=this._rowData;
    if (!rows) {
      return this.paramsList = [];
    }

    // removing parameters with not valid data.
    rows = rows.filter((row) => (row.metadata && row.metadata.paramName && !row.isRollupHeader));

    //set default
    rows.forEach((row)=>{
      if(this.primaryAxisParams[this.contactType][this.getParamName(row)]===undefined){
        this.primaryAxisParams[this.contactType][this.getParamName(row)]=true;
      }
    })

    this.paramsList = this.distinctRows(rows).map((row) => {
      return {
        display: row.plan_label_param,
        isChecked: this.selectedParams[this.contactType][this.getParamName(row)]==true,
        isPrimary: this.primaryAxisParams[this.contactType][this.getParamName(row)]===true,
        data:row,
        isNested:row.rowChild,
        isRollup: row.entityInfo.isRollup
      };
    });
  }
  private distinctRows(rows){
    const seen = new Set();

    return rows.filter(row => {
      let paramName = this.getParamName(row);
      if (seen.has(paramName)) {
        return false;
      } else {
        seen.add(paramName);
        return true;
      }
    });
  }
  private onDrillDown() {
    if(this.gridContext.canDrillDown && this.gridContext.canDrillDown()) {
      this.drillDown.emit();
    }
  }

  public onRollUp(){
    this.rollUp.emit();
  }
  public canRollUp(){
    return this._viewMode!=DetailViewMode.Monthly
  }

  private getColumnDefinitionsFlat() {
    let columnDefsFlat = [];
    this._columnDefs.forEach((colDef) => {
      if (colDef.children) {
        colDef.children.forEach((colDefChild) => {
          columnDefsFlat.push(colDefChild);
        });
      } else {
        columnDefsFlat.push(colDef);
      }
    });
    if(this.contactType=="CT"){
      return columnDefsFlat.slice(1, -2); //remove first column, average and total
    }else{
      return columnDefsFlat.slice(2, -2); //remove group column, parameter name column, average and total
    }
  }

  private buildSeriesData(row, columnDefsFlat): any[] {
    return columnDefsFlat.map((column) => {
      let value = row[column.field] ? row[column.field] : null;
      if (value == "???") {
        value = null;
      }
      return {
        value: value != null ? parseFloat(value) : null,
        name: column.headerNameFull,
        _metadata: row.metadata.cellmetadata[column.field]
      };
    });
  }

  private buildCTSeries(row, columnDefsFlat, isPrimaryAxis): any {
    let isDraggable = this.gridContext.cellEditable({data:row});
    let rowIndex = this._rowData.indexOf(row);

    this._rowsParamMap[row.metadata.paramName]=row;

    return this.echartsConfig.buildSeries({
      name: row.plan_label_param,
      id: row.metadata.paramName,
      data: this.buildSeriesData(row, columnDefsFlat),
      isPrimary: isPrimaryAxis,
      isDraggable,
      color: this.echartsConfig.getColor(rowIndex),
      decimal: row.metadata.decimal,
      metadata: { row }
    });
  }

  private buildMUSeriesArray(paramRow, columnDefsFlat): any[] {
    let paramName = paramRow.metadata.paramName;
    let isPlanRollup = paramRow.entityInfo.isRollup;
    let hasMUrows = this._rowData.some((row)=>row.entityInfo.isRollup);
    if(hasMUrows==false){
      return [];
    }
    let seriesArr = [];
    this._rowData.forEach((row)=>{
      if(row.metadata.paramName === paramName && row.entityInfo.isRollup==isPlanRollup){
        seriesArr.push(this.buildMUSeries(row, columnDefsFlat));
      }
    });
    return seriesArr;
  }

  private buildMUSeries(row, columnDefsFlat): any {
    let isDraggable = this.gridContext.cellEditable({data:row});
    let muName = this.getMUName(row);
    let muId = this.getMUId(row);
    let seriesId = this.getSeriesId(row);
    this._rowsParamMap[seriesId] = row;

    return this.echartsConfig.buildSeries({
      name: muName,
      id: seriesId,
      data: this.buildSeriesData(row, columnDefsFlat),
      isPrimary: true,
      isDraggable,
      color: this.echartsConfig.getColor(muId),
      decimal: row.metadata.decimal,
      metadata: { row }
    });
  }

  private getXAxisStyle(): any {
    const canDrill = this._viewMode !== DetailViewMode.Daily
      && !(this.gridContext.canDrillDown && this.gridContext.canDrillDown() === false);
    if (canDrill) {
      return {
        triggerEvent: true,
        axisLabel: {
          show: true,
          fontSize: 12,
          formatter: (value: string) => `{text|${value}}\n{line|${value}}`,
          rich: {
            text: {
              color: '#0094D1',
              fontSize: 12,
              lineHeight: 16
            },
            line: {
              color: 'transparent',
              fontSize: 12,
              height: 1,
              backgroundColor: '#0094D1'
            }
          }
        }
      };
    }
    return {
      triggerEvent: false,
      axisLabel: {
        show: true,
        color: 'black',
        fontSize: 12,
        formatter: '{value}',
        rich: {}
      }
    };
  }

  public updateChart() {
    if (!this._columnDefs || this._columnDefs.length == 0 || !this._rowData || !this.chart || this.chartId===null) {
      return;
    }

    this._isUpdatingChart = true;

    // Clear emphasis/highlight state before updating
    this.chart.dispatchAction({ type: 'downplay' });
    this.chart.dispatchAction({ type: 'hideTip' });
    this.hideCrosshair();

    this.updateParamsList();
    this.paramsListChanged.emit(this.paramsList);

    this._colDefsFlat = this.getColumnDefinitionsFlat();
    this._rowsParamMap = {};

    // Build categories
    let categories = this._colDefsFlat.map((column) => column.headerNameFull);

    // Build series
    let seriesArr = [];

    this.paramsList.forEach((checkBoxItem) => {
      if(checkBoxItem.isChecked){
        if(this.contactType=="CT"){
          seriesArr.push(this.buildCTSeries(checkBoxItem.data, this._colDefsFlat, checkBoxItem.isPrimary));
        }else{
          seriesArr.push(...this.buildMUSeriesArray(checkBoxItem.data, this._colDefsFlat));
        }
        this.updateYAxisTitle(checkBoxItem.data);
      }
    });

    // Reset zoom if needed
    let resetZoom = this._shouldResetZoom;
    if(this._shouldResetZoom){
      this._shouldResetZoom=false;
    }

    const xAxisStyle = this.getXAxisStyle();
    if (this.dragHandler) {
      this.dragHandler.drillDownEnabled = !(this.gridContext.canDrillDown && this.gridContext.canDrillDown() == false);
    }

    // Rotate labels only when they would overlap
    const chartWidth = this.chart.getWidth();
    const labelSpace = chartWidth / Math.max(categories.length, 1);
    const labelRotate = labelSpace < 80 ? 45 : 0;

    const updateOption: any = {
      animation: false,
      xAxis: {
        data: categories,
        show: this.displayFooter,
        ...xAxisStyle,
        axisLabel: {
          ...(xAxisStyle.axisLabel || {}),
          rotate: labelRotate
        }
      },
      yAxis: [
        { show: seriesArr.some(s => s.yAxisIndex === 0), nameGap: this.getYAxisNameGap(seriesArr) },
        { show: seriesArr.some(s => s.yAxisIndex === 1) }
      ],
      grid: { bottom: this.getLegendGridBottom(seriesArr, chartWidth) },
      series: seriesArr
    };
    if (resetZoom) {
      updateOption.dataZoom = [
        { start: 0, end: 100 }
      ];
    }
    this.chart.setOption(updateOption, { replaceMerge: ['series'] });

    this._skipAnimation=false;
    this.chart.resize();
    this.hideTooltips();

    // Clear any emphasis/tooltip that gets re-applied after render
    setTimeout(() => {
      if (this.chart) {
        this.chart.dispatchAction({ type: 'downplay' });
        this.chart.dispatchAction({ type: 'hideTip' });
      }
      this._isUpdatingChart = false;
    });
  }

  onParamSelectionChange(item: CheckboxListItem) {
    let row = item.data;

    this.selectedParams[this.contactType][this.getParamName(row)]=item.isChecked;
    this.primaryAxisParams[this.contactType][this.getParamName(row)]=item.isPrimary;
    this.saveParamSelection();

    // Rebuild all series
    this._colDefsFlat = this.getColumnDefinitionsFlat();
    this._rowsParamMap = {};

    let seriesArr = [];

    this.paramsList.forEach((checkBoxItem) => {
      if (checkBoxItem.data === row) {
        checkBoxItem.isChecked = item.isChecked;
        checkBoxItem.isPrimary = item.isPrimary;
      }
      if(checkBoxItem.isChecked){
        if(this.contactType=="CT"){
          seriesArr.push(this.buildCTSeries(checkBoxItem.data, this._colDefsFlat, checkBoxItem.isPrimary));
        }else{
          seriesArr.push(...this.buildMUSeriesArray(checkBoxItem.data, this._colDefsFlat));
        }
        this.updateYAxisTitle(checkBoxItem.data);
      }
    });

    this.chart.setOption({
      yAxis: [
        { show: seriesArr.some(s => s.yAxisIndex === 0), nameGap: this.getYAxisNameGap(seriesArr) },
        { show: seriesArr.some(s => s.yAxisIndex === 1) }
      ],
      grid: { bottom: this.getLegendGridBottom(seriesArr, this.chart.getWidth()) },
      series: seriesArr
    }, { replaceMerge: ['series'] });
  }

  private loadParamSelection(){
    let localData = localStorage.getItem("ChartParamsV2-"+this.chartId);
    if(localData){
      this.selectedParams = JSON.parse(localData);
    }

    localData = localStorage.getItem("PrimaryChartParamsV2-"+this.chartId);
    if(localData) {
      this.primaryAxisParams = JSON.parse(localData);
    }
  }

  private saveParamSelection(){
    localStorage.setItem("ChartParamsV2-"+this.chartId,JSON.stringify(this.selectedParams));
    localStorage.setItem("PrimaryChartParamsV2-"+this.chartId,JSON.stringify(this.primaryAxisParams));
  }

  /**
   * Migrates MU chart parameter selection between legacy and new hire parameters
   * based on the new hire feature flag state
   */
  private migrateMuParameterSelection(): void {
    if (this.shouldMigrateToNewHireParameter()) {
      this.migrateToNewHireParameter();
    } else if (this.shouldRevertToLegacyParameter()) {
      this.revertToLegacyParameter();
    }
  }

  /**
   * Checks if we should migrate from legacy ACTUAL_FTE_rollup to new hire ACTUAL_FTE_TOTAL_rollup parameter
   */
  private shouldMigrateToNewHireParameter(): boolean {
    return this._newHireSettingsFeature === true &&
           this.selectedParams["MU"]["ACTUAL_FTE_rollup"] === true;
  }

  /**
   * Checks if we should revert from new hire parameters back to legacy ACTUAL_FTE_rollup parameter
   */
  private shouldRevertToLegacyParameter(): boolean {
    if (this._newHireSettingsFeature !== false) {
      return false;
    }

    const newHireParameters = [
      "ACTUAL_FTE_TOTAL_rollup",
      "ACTUAL_FTE_TOTAL",
      "FTE_ACTUAL_NEW_HIRE",
      "FTE_TOTAL_rollup",
      "FTE_TOTAL"
    ];

    return newHireParameters.some(param => this.selectedParams["MU"][param] === true);
  }

  /**
   * Migrates from legacy ACTUAL_FTE_rollup to new hire ACTUAL_FTE_TOTAL_rollup parameter
   */
  private migrateToNewHireParameter(): void {
    this.selectedParams["MU"]["ACTUAL_FTE_rollup"] = false;
    this.selectedParams["MU"]["ACTUAL_FTE_TOTAL_rollup"] = true;
    this.saveParamSelection();
  }

  /**
   * Reverts from new hire parameters back to legacy ACTUAL_FTE_rollup parameter
   */
  private revertToLegacyParameter(): void {
    // Disable all new hire parameters
    this.selectedParams["MU"]["ACTUAL_FTE_TOTAL_rollup"] = false;
    this.selectedParams["MU"]["ACTUAL_FTE_TOTAL"] = false;
    this.selectedParams["MU"]["FTE_ACTUAL_NEW_HIRE"] = false;
    this.selectedParams["MU"]["FTE_TOTAL"] = false;
    this.selectedParams["MU"]["FTE_TOTAL_rollup"] = false;

    // Enable legacy parameter
    this.selectedParams["MU"]["ACTUAL_FTE_rollup"] = true;
    this.saveParamSelection();
  }

  private onValueSet(seriesIndex: number, dataIndex: number, newValue: number) {
    const option = this.chart.getOption() as any;
    const series = option.series[seriesIndex];
    const category = (option.xAxis[0].data || [])[dataIndex];

    let colKey = this._colDefsFlat.filter((col) => col.headerNameFull == category);
    let row = null;
    if(this.contactType==="CT"){
      row = this._rowData.find((row) => row.metadata.paramName == series.id);
    }else{
      row = this._rowData.find((row) => series.id == this.getSeriesId(row));
    }

    this._skipAnimation=true;
    if (colKey.length > 0 && row !== null) {
      this.gridContext.setCustomValue(row, colKey[0].field, newValue);
    }
  }

  highlight($event: any) {
    if (!this.chart || this._isUpdatingChart) return;

    const dataIndex = $event.dataIndex;
    if (dataIndex != null) {
      this.ignoreHighlight = true;
      // Switch to axis trigger so tooltip shows all series at this x position
      this.chart.setOption({ tooltip: { trigger: 'axis' } } as any);
      try {
        const point = this.chart.convertToPixel({ seriesIndex: 0 }, [dataIndex, 0]);
        if (point) {
          this.chart.dispatchAction({
            type: 'showTip',
            x: point[0],
            y: point[1]
          });
        }
      } catch {
        this.chart.dispatchAction({
          type: 'showTip',
          seriesIndex: 0,
          dataIndex: dataIndex
        });
      }
      this.showCrosshair({ seriesIndex: 0, dataIndex: dataIndex });
    }
  }

  removeHighlight($event: any) {
    if (!this.chart || this._isUpdatingChart) return;

    this.ignoreHighlight = true;
    this.chart.dispatchAction({ type: 'hideTip' });
    // Restore item trigger after cross-chart sync
    this.chart.setOption({ tooltip: { trigger: 'item' } } as any);
    this.hideCrosshair();
    // Reset flag so the next real user hover on this chart is not swallowed
    this.ignoreHighlight = false;
  }

  hideTooltips(){
    if (!this.chart) return;
    this.chart.dispatchAction({ type: 'hideTip' });
    this.chart.setOption({ tooltip: { trigger: 'item' } } as any);
    this.hideCrosshair();
  }

  private showCrosshair(params: any): void {
    if (!this.chart) return;
    try {
      const point = this.chart.convertToPixel({ seriesIndex: params.seriesIndex }, [params.dataIndex, 0]);
      if (!point) return;
      const gridModel = (this.chart as any).getModel().getComponent('grid', 0);
      const gridRect = gridModel?.coordinateSystem?.getRect?.();
      if (!gridRect) return;

      this.chart.setOption({
        graphic: [{
          id: 'crosshairLine',
          type: 'line',
          z: 50,
          silent: true,
          shape: { x1: point[0], y1: gridRect.y, x2: point[0], y2: gridRect.y + gridRect.height },
          style: { stroke: '#c1c1c1', lineWidth: 2 }
        }]
      } as any);
    } catch {}
  }

  private hideCrosshair(): void {
    if (!this.chart) return;
    try {
      this.chart.setOption({
        graphic: [{ id: 'crosshairLine', $action: 'remove' }]
      } as any);
    } catch {}
  }

  // The legend wraps onto multiple rows (type: 'plain') instead of paginating, so grid.bottom
  // must grow with however many rows the current chart width wraps the series names into, or
  // the wrapped rows overlap the x-axis.
  private getLegendGridBottom(seriesArr: any[], chartWidth: number): number {
    const names = (seriesArr || []).map(s => s.name).filter(Boolean);
    const rows = this.echartsConfig.countLegendRows(names, chartWidth);
    return this.echartsConfig.getLegendGridBottom(rows);
  }

  // The primary (left) y-axis shows a rotated "Values"/param name via updateYAxisTitle(); size
  // its distance from the axis line to the widest tick label actually on that axis, so wide
  // numbers (e.g. "-10,000,000") don't render underneath the title text.
  private getYAxisNameGap(seriesArr: any[]): number {
    const values = (seriesArr || [])
      .filter(s => s.yAxisIndex === 0)
      .flatMap(s => s.data || [])
      // buildSeriesData() wraps each point as {value, name, _metadata}, not a raw number -
      // isNaN() on that object coerces to NaN and silently drops every point, so unwrap first.
      .map(d => (d && typeof d === 'object') ? d.value : d)
      .filter(v => v != null && !isNaN(v));
    return this.echartsConfig.getYAxisNameGap(values, this.decimalPoint, this.thousandsSep);
  }

  syncExtremes(e) {
    if (!this.chart) return;
    this.ignoreExtremes = true;

    this.chart.dispatchAction({
      type: 'dataZoom',
      start: e.start,
      end: e.end
    });
  }

  resetZoom() {
    if (!this.chart) return;
    this.chart.dispatchAction({
      type: 'dataZoom',
      start: 0,
      end: 100
    });
    this.isZoomed = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.showExportMenu) return;
    const target = event.target as HTMLElement;
    if (!target.closest('.chart-export-menu') && !target.closest('.btnExport')) {
      this.showExportMenu = false;
    }
  }

  toggleExportMenu() {
    this.showExportMenu = !this.showExportMenu;
  }

  onExport(type: string) {
    this.showExportMenu = false;
    if (!this.chart) return;

    switch (type) {
      case 'png': this.chartExportService.exportPNG(this.chart); break;
      case 'jpeg': this.chartExportService.exportJPEG(this.chart); break;
      case 'svg': this.chartExportService.exportSVG(this.chart); break;
      case 'pdf': this.chartExportService.exportPDF(this.chart); break;
      case 'print': this.chartExportService.printChart(this.chart); break;
      case 'fullscreen':
        if (this.chartEl?.nativeElement) {
          this.chartExportService.viewFullscreen(this.chartEl.nativeElement);
        }
        break;
    }
  }

  onDisplayParams(){
    this.displayParams.emit(this.chartId);
  }
  onChartMouseLeave(){
    // No-op for ECharts
  }
  onChartMouseEnter() {
    // No-op for ECharts
  }
  getParamName(row){
    return row.entityInfo.isRollup ? row.metadata.paramName + "_rollup":row.metadata.paramName
  }
  getMUId(row){
    let object = row.entityInfo.object;
    if(object){
      return object.id;
    }else{
      return -1;
    }
  }
  getMUName(row){
    if (row.entityInfo.isRollup) {
      return this.translateSrv.instant('multi.mu.rollup.group.header');
    }
    let object = row.entityInfo.object;
    if(object){
      return this.tenantService.isCXOne? object.name : `${object.id} ${object.name}`;
    }else{
      return row.plan_label_param;
    }
  }

  getSeriesId(row){
    return row.metadata.paramName + "_" + row.entityInfo.value.oid;
  }
  updateYAxisTitle(row) {
    if (!this.chart) return;
    const text = this.contactType === "CT"
      ? this.translateSrv.instant("chart.yAxis.values")
      : this.getYAxisLabel(row);
    // nameGap is sized separately (see getYAxisNameGap) from the full series data range, since
    // this function only sees one row/series at a time.
    this.chart.setOption({
      yAxis: [{
        name: text,
        nameLocation: 'middle',
        nameRotate: 90,
        nameTextStyle: { color: EChartsConfigService.PRIMARY_AXIS_COLOR }
      }]
    } as any);
  }

  getYAxisLabel(row) {
    const isCXone = this.tenantService.isCXOne;
    const prefix = isCXone ? this.translateSrv.instant("plan.details.mu.view.label") : this.translateSrv.instant("plan.details.mu.view");
    return prefix + " - " +  row.plan_label_param;
  }
}
